import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import gql from "graphql-tag";

import { Background } from "components/Background/Backgound";
import { Spinner } from "components/Spinner/Spinner";
import { ConsoleAnalyticsDashboards } from "generated/copilot-api-developer";
import { useQuery } from "graphql-client/useQuery";
import { useTranslation } from "i18n";

import { LookerDashboard } from "./LookerDashboard";

gql`
  # schema = COPILOT_API_DEVELOPER
  query ConsoleAnalyticsDashboards {
    me {
      developer {
        organization {
          analyticsDashboards {
            uuid
            name
            url
          }
        }
      }
    }
  }
`;

export const Analytics = () => {
  const trans = useTranslation();
  const { data, loading } = useQuery(ConsoleAnalyticsDashboards);
  const dashboards = useMemo(
    () => data?.developer.organization.analyticsDashboards ?? [],
    [data?.developer.organization.analyticsDashboards],
  );
  const [selectedDashboardUuid, setSelectedDashboardUuid] = useState<
    string | undefined
  >(dashboards[0]?.uuid);
  const selectedDashboard =
    dashboards.find((dashboard) => dashboard.uuid === selectedDashboardUuid) ??
    dashboards[0];

  useEffect(() => {
    if (selectedDashboardUuid === undefined && dashboards[0]) {
      setSelectedDashboardUuid(dashboards[0].uuid);
    }
  }, [dashboards, selectedDashboardUuid]);

  return (
    <Background className="flex-col flex-fill overflow-hidden p-16 lg:p-44 space-y-24">
      <div className="flex-col space-y-16">
        <h1 className="text-primary-dark text-24 font-bold">
          {trans("developers.analytics.title")}
        </h1>
        {dashboards.length > 1 && (
          <div className="flex gap-32 border-b">
            {dashboards.map((dashboard) => (
              <button
                key={dashboard.uuid}
                className={classNames(
                  "pb-12 border-b-2 font-medium",
                  dashboard.uuid === selectedDashboard.uuid
                    ? "border-primary text-primary-dark"
                    : "border-transparent text-grey-300",
                )}
                type="button"
                onClick={() => setSelectedDashboardUuid(dashboard.uuid)}
              >
                {dashboard.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex-fill flex-center">
          <Spinner />
        </div>
      ) : dashboards.isEmpty() ? (
        <div className="flex-fill flex-center text-grey-300">
          {trans("developers.analytics.no_dashboards")}
        </div>
      ) : (
        <LookerDashboard
          key={selectedDashboard.uuid} // make sure the component is unmounted when the dashboard URL changes
          analyticsDashboardUrl={selectedDashboard.url}
        />
      )}
    </Background>
  );
};
